import type { CoverageRow, Progress, Topic } from "../../services/curriculumService";
import { byNumber, byText, reversed, type RegisterConfig } from "../register/registerLogic";

/**
 * Curriculum coverage: how far each class has got through each syllabus, from
 * what teachers record as they teach. "Behind" mirrors apps/curriculum/coverage.is_behind:
 * a topic still under way after the weeks it was planned for.
 */

export const day = (value?: string | null) => (value ? value.slice(0, 10) : "");

export function coverageStatus(row: Pick<CoverageRow, "coverage" | "behind" | "completed" | "in_progress" | "topic_count">) {
  if (!row.topic_count) return { label: "No topics yet", dot: "bg-gray-400" };
  if (row.behind) return { label: `${row.behind} behind`, dot: "bg-red-500" };
  if (row.completed === row.topic_count) return { label: "Finished", dot: "bg-emerald-500" };
  if (!row.completed && !row.in_progress) return { label: "Not started", dot: "bg-gray-400" };
  return { label: "On track", dot: "bg-sky-500" };
}

export const coverageConfig: RegisterConfig<CoverageRow> = {
  search: (row) => [row.class_name, row.subject_name, row.syllabus_title, row.grade_name, ...row.teachers].join(" "),
  views: [
    { key: "all", label: "All" },
    { key: "mine", label: "I teach", match: (row) => row.can_record },
    { key: "behind", label: "Behind", match: (row) => row.behind > 0 },
    { key: "unstarted", label: "Not started", match: (row) => row.topic_count > 0 && !row.completed && !row.in_progress },
    { key: "finished", label: "Finished", match: (row) => row.topic_count > 0 && row.completed === row.topic_count },
  ],
  category: { label: "Class", value: (row) => row.class_name },
  sorts: [
    { key: "coverage", label: "Least covered", compare: (a, b) => a.coverage - b.coverage || a.class_name.localeCompare(b.class_name) },
    { key: "-behind", label: "Most behind", compare: reversed(byNumber<CoverageRow>((row) => row.behind)) },
    { key: "class", label: "Class, then subject", compare: byText<CoverageRow>((row) => `${row.class_name} ${row.subject_name}`) },
    { key: "-updated", label: "Recently updated", compare: reversed(byText<CoverageRow>((row) => row.last_updated ?? "")) },
  ],
};

export type TopicState = "not_started" | "in_progress" | "behind" | "completed";

export function topicState(record?: Pick<Progress, "status" | "is_behind">): TopicState {
  if (!record) return "not_started";
  if (record.status === "completed") return "completed";
  return record.is_behind ? "behind" : "in_progress";
}

export const TOPIC_STATES: Record<TopicState, { label: string; dot: string }> = {
  not_started: { label: "Not started", dot: "bg-gray-400" },
  in_progress: { label: "Under way", dot: "bg-sky-500" },
  behind: { label: "Behind", dot: "bg-red-500" },
  completed: { label: "Done", dot: "bg-emerald-500" },
};

/** A syllabus's topics in teaching order, each with this class's record if there is one. */
export function topicPlan(topics: Topic[], records: Progress[]) {
  const byTopic = new Map(records.map((record) => [record.topic, record]));
  return [...topics]
    .sort((a, b) => a.order - b.order || a.name.localeCompare(b.name))
    .map((topic) => ({ topic, record: byTopic.get(topic.id), state: topicState(byTopic.get(topic.id)) }));
}

/** The next topic to start: the first in order that has not been. */
export const nextTopic = (plan: ReturnType<typeof topicPlan>) => plan.find((row) => row.state === "not_started")?.topic;

/** Weeks under way, to set against the weeks planned. */
export function weeksSince(start: string, today: string) {
  const days = (new Date(`${today}T00:00:00`).getTime() - new Date(`${start}T00:00:00`).getTime()) / 86_400_000;
  return Math.max(0, Math.floor(days / 7));
}

export const recordConfig = (me: number | undefined): RegisterConfig<Progress> => ({
  search: (row) => [row.topic_name, row.subject_name, row.class_name, row.teacher_name, row.notes, row.achievements, row.challenges].join(" "),
  views: [
    { key: "open", label: "Under way", match: (row) => row.status !== "completed" },
    { key: "behind", label: "Behind", match: (row) => row.is_behind },
    { key: "done", label: "Done", match: (row) => row.status === "completed" },
    { key: "mine", label: "Mine", match: (row) => row.teacher === me },
    { key: "all", label: "All" },
  ],
  date: { label: "Started", value: (row) => row.start_date },
  category: { label: "Subject", value: (row) => row.subject_name },
  sorts: [
    { key: "-updated", label: "Recently updated", compare: reversed(byText<Progress>((row) => row.updated_at)) },
    { key: "start", label: "Started first", compare: byText<Progress>((row) => row.start_date) },
    { key: "class", label: "Class, then topic", compare: byText<Progress>((row) => `${row.class_name} ${row.subject_name} ${String(row.topic_order).padStart(4, "0")}`) },
  ],
});
